<template>
  <div v-if="show" class="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="p-6 border-b">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold">Upload Assessment</h2>
          <button
            @click="closeStepper"
            class="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="p-6 space-y-8">
        <div>
          <h3 class="text-xl font-semibold mb-4 text-gray-800">Upload Assessment</h3>
          <NewAssessementUploadForm 
            :show="true"
            :embedded="true"
            :auto-close="false"
            @close-modal="closeStepper"
            @assessment-created="handleAssessmentCreated"
          />
          <p class="mt-4 text-sm text-gray-500">
            Tokens can only be uploaded for CPD accredited assessments after the assessment is saved.
          </p>
        </div>

        <div v-if="showTokensSection" class="border-t pt-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-xl font-semibold text-gray-800">Upload CPD Tokens</h3>
              <p class="text-sm text-gray-500">
                Tokens are required because this assessment is CPD accredited.
              </p>
            </div>
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              CPD Accredited
            </span>
          </div>
          <UploadTokensForm 
            :assessment-id="currentAssessmentId"
            @upload-complete="handleTokensComplete"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import NewAssessementUploadForm from '../views/forms/NewAssessementUploadForm.vue';
import UploadTokensForm from '../views/forms/UploadTokensForm.vue';

export default {
  name: 'AssessmentUploadStepper',
  components: {
    NewAssessementUploadForm,
    UploadTokensForm
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    assessmentId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['close', 'refresh'],
  setup(props, { emit }) {
    const currentAssessmentId = ref(null);
    const isCpdAccredited = ref(false);

    const showTokensSection = ref(false);

    const closeStepper = () => {
      currentAssessmentId.value = null;
      isCpdAccredited.value = false;
      showTokensSection.value = false;
      emit('close');
    };

    const extractAssessmentPayload = (assessmentData) => {
      if (!assessmentData) return {};
      if (assessmentData.data && typeof assessmentData.data === 'object') {
        return assessmentData.data;
      }
      if (assessmentData.assessment) {
        return assessmentData.assessment;
      }
      return assessmentData;
    };

    const handleAssessmentCreated = (assessmentData) => {
      const payload = extractAssessmentPayload(assessmentData);
      const resolvedId = payload.assessment_id || payload.id || assessmentData.assessment_id || assessmentData.id;

      currentAssessmentId.value = resolvedId;
      const cpdValue = payload.cpd_accredited ?? assessmentData.cpd_accredited;
      isCpdAccredited.value = String(cpdValue).toLowerCase() === 'yes' || cpdValue === true;
      showTokensSection.value = Boolean(currentAssessmentId.value && isCpdAccredited.value);

      // If assessment is created and tokens section is not needed, close modal and refresh
      if (!showTokensSection.value) {
        console.info('Assessment created without CPD accreditation. Tokens upload not required.');
        // Close modal and emit refresh event
        setTimeout(() => {
          closeStepper();
          emit('refresh');
        }, 1000); // Small delay to allow success message to be shown
      }
    };

    const handleTokensComplete = () => {
      // Close modal and emit refresh event after successful token upload
      setTimeout(() => {
        closeStepper();
        emit('refresh');
      }, 1000); // Small delay to allow success message to be shown
    };

    return {
      currentAssessmentId,
      showTokensSection,
      closeStepper,
      handleAssessmentCreated,
      handleTokensComplete
    };
  }
};
</script>
